import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Button, Typography, CircularProgress, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { getDeviceId } from '@app/shared/utils/device-id';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import moment from 'moment';

const VALIDATE_COUPON_URL = 'https://europe-west3-tours-app-1579553856346.cloudfunctions.net/validateCoupon';

// --- Types ---

type CouponStep =
	| 'not-available'
	| 'available'
	| 'confirm-reveal'
	| 'scan-barcode'
	| 'enter-pin'
	| 'revealed'
	| 'already-used';

type CouponPageProps = {
	media: any;
	onClose: () => void;
	onClaimUpdate?: (claimedAt: string | null) => void;
};

// --- Styles ---

const COLORS = {
	primary: '#C4702B',
	primaryDark: '#8B4513',
	bg: '#FAF6F1',
	cardBg: '#FFFFFF',
	warningBg: 'rgba(196, 112, 43, 0.08)',
	warningBorder: '#C4702B',
	successBg: 'rgba(28, 155, 25, 0.12)',
	errorBg: 'rgba(155, 25, 25, 0.08)',
	errorBorder: '#9B1919',
	disabledBg: '#C4B5A5',
	textPrimary: '#2D2D2D',
	textSecondary: '#666666',
	scannerBg: '#1A1A1A',
	scannerFrame: '#C4702B',
	scannerError: '#D32F2F',
} as const;

// --- Helpers ---

function parseTimeToMinutes(time: string): number {
	const [h, m] = time.split(':').map(Number);
	return h * 60 + (m || 0);
}

export function isCurrentlyWithinHours(availableHours: { start: string; end: string } | null | undefined): boolean {
	if (!availableHours || !availableHours.start || !availableHours.end) return true; // No hours = always available
	const now = new Date();
	const currentMinutes = now.getHours() * 60 + now.getMinutes();
	return currentMinutes >= parseTimeToMinutes(availableHours.start) && currentMinutes <= parseTimeToMinutes(availableHours.end);
}

export function formatHoursRange(availableHours: { start: string; end: string } | null | undefined): string {
	if (!availableHours || !availableHours.start || !availableHours.end) return '';
	return `${availableHours.start} – ${availableHours.end}`;
}

// --- Countdown Timer Hook ---

export function useCountdownTimer(claimedAt: string | undefined, onExpire: () => void) {
	const [timeLeft, setTimeLeft] = useState('');
	const [isExpired, setIsExpired] = useState(false);

	useEffect(() => {
		if (!claimedAt) return;

		const DURATION_MS = 12 * 60 * 1000;

		const tick = () => {
			const elapsed = moment().diff(moment(new Date(claimedAt)));
			if (elapsed >= DURATION_MS) {
				setTimeLeft('00:00');
				setIsExpired(true);
				onExpire();
				return true;
			}
			const remaining = DURATION_MS - elapsed;
			const mins = Math.floor(remaining / 60000);
			const secs = Math.floor((remaining % 60000) / 1000);
			setTimeLeft(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
			return false;
		};

		// Initial tick
		const expired = tick();
		if (expired) return;

		const id = setInterval(() => {
			if (tick()) clearInterval(id);
		}, 1000);

		return () => clearInterval(id);
	}, [claimedAt, onExpire]);

	return { timeLeft, isExpired };
}

// --- Main Component ---

export const CouponPage: React.FC<CouponPageProps> = React.memo(function CouponPage({ media, onClose, onClaimUpdate }) {
	const { t, i18n } = useTranslation();
	const state_activeStopState = useRecoilValue(atom_activeStopState);
	const isRTL = i18n.language === 'he';

	// --- Coupon State ---
	const [claimData, setClaimData] = useState<{ claimedAt: string; amount: number } | null>(null);
	const [tasteCount, setTasteCount] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [couponStep, setCouponStep] = useState<CouponStep>('available');

	// Backend response data
	const [qrPayload, setQrPayload] = useState<string | null>(null);
	const [fallbackPin, setFallbackPin] = useState<string | null>(null);

	// Scanner state
	const [scanError, setScanError] = useState('');
	const scannerRef = useRef<Html5Qrcode | null>(null);
	const scannerContainerId = 'coupon-barcode-scanner';

	// PIN state
	const [pinDigits, setPinDigits] = useState<string[]>(['', '', '', '', '', '']);
	const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// Online state
	const [isOnline, setIsOnline] = useState(navigator.onLine);

	// --- Derived values ---

	const isWithinHours = useMemo(() => isCurrentlyWithinHours(media.availableHours), [media.availableHours]);

	const openingTime = media.availableHours?.start || '';

	const hoursRangeDisplay = useMemo(() => formatHoursRange(media.availableHours), [media.availableHours]);

	const formattedRevealDate = useMemo(() => {
		if (!claimData?.claimedAt) return '';
		return moment(claimData.claimedAt).format('DD MMM YYYY, HH:mm');
	}, [claimData?.claimedAt]);

	const partnerName = media.product_name || 'Partner';

	// Old coupons have no businessId — skip QR/PIN, reveal directly
	const isNewCoupon = !!media.businessId;

	// --- Timer ---

	const handleTimerExpire = useCallback(() => {
		setCouponStep('already-used');
	}, []);

	const { timeLeft, isExpired } = useCountdownTimer(
		couponStep === 'revealed' ? claimData?.claimedAt : undefined,
		handleTimerExpire
	);

	// --- Online/offline tracking ---

	useEffect(() => {
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);
		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);
		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

	// --- API: validateCoupon (single endpoint, multiple modes) ---

	// Base request fields shared by all modes
	const getBaseData = useCallback(() => {
		const { uID, tID, gID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
		return {
			gID,
			tID,
			uID,
			uniqueID: getDeviceId(),
			stopID: state_activeStopState?.id,
			debug: true,
		};
	}, [state_activeStopState?.id]);

	// Helper to handle common response logic
	const handleCouponResponse = useCallback((res: any) => {
		const data = res.data;

		// Store verification methods from response
		if (data.qrPayload) setQrPayload(data.qrPayload);
		if (data.fallbackPin) setFallbackPin(data.fallbackPin);

		// Already redeemed — restore timer if the 12-min window is still active,
		// otherwise show already-used. This applies on initial load too, so reopening
		// from StopDetails lands back on the running timer.
		if (data.expiredTime) {
			const expiredTime = moment(data.expiredTime).add(12, 'minutes');
			setClaimData({ claimedAt: data.expiredTime, amount: data.available });
			if (expiredTime.isBefore(moment())) {
				setCouponStep('already-used');
				return 'already-used';
			}
			setCouponStep('revealed');
			return 'revealed';
		}

		const count = data.available || 0;
		if (count <= 0) {
			setClaimData({ claimedAt: moment().subtract(1, 'day').toISOString(), amount: 0 });
			setCouponStep('already-used');
			return 'already-used';
		}
		setTasteCount(count);
		return 'available';
	}, []);

	// Mode 1: Check availability only
	const checkAvailability = useCallback(async () => {
		setIsLoading(true);
		try {
			const baseData = getBaseData();
			console.log('=== validateCoupon request ===', baseData);
			const res = await axios.post(VALIDATE_COUPON_URL, baseData);
			console.log('=== validateCoupon response ===', res.data);
			handleCouponResponse(res);
		} catch (err) {
			console.log({ err });
		} finally {
			setIsLoading(false);
		}
	}, [getBaseData, handleCouponResponse]);

	// Mode 2: Verify barcode (sends scanned QR value to backend)
	const verifyBarcode = useCallback(async (barcode: string): Promise<boolean> => {
		try {
			const res = await axios.post(VALIDATE_COUPON_URL, { ...getBaseData(), barcode });
			if (res.data.verified) {
				return true;
			}
			setScanError(res.data.reason || "Barcode doesn't match");
			return false;
		} catch (err) {
			console.log({ err });
			return false;
		}
	}, [getBaseData]);

	// Mode 3: Verify PIN (sends PIN to backend)
	const verifyPin = useCallback(async (pin: string): Promise<{ verified: boolean; reason?: string }> => {
		try {
			const res = await axios.post(VALIDATE_COUPON_URL, { ...getBaseData(), pin });
			return { verified: !!res.data.verified, reason: res.data.reason };
		} catch (err) {
			console.log({ err });
			return { verified: false, reason: 'Network error' };
		}
	}, [getBaseData]);

	// Mode 4: Verify + redeem (barcode/pin + date in one call)
	const verifyAndRedeem = useCallback(async (verification: { barcode?: string; pin?: string }) => {
		setIsLoading(true);
		try {
			const res = await axios.post(VALIDATE_COUPON_URL, {
				...getBaseData(),
				...verification,
				date: new Date().toISOString(),
			});
			if (res.data.verified === false) {
				setScanError(res.data.reason || 'Verification failed');
				return false;
			}
			handleCouponResponse(res);
			return true;
		} catch (err) {
			console.log({ err });
			return false;
		} finally {
			setIsLoading(false);
		}
	}, [getBaseData, handleCouponResponse]);

	// Old coupon: redeem directly (no verification needed)
	const redeemOldCoupon = useCallback(async () => {
		setIsLoading(true);
		try {
			const res = await axios.post(VALIDATE_COUPON_URL, {
				...getBaseData(),
				date: new Date().toISOString(),
			});
			handleCouponResponse(res);
		} catch (err) {
			console.log({ err });
		} finally {
			setIsLoading(false);
		}
	}, [getBaseData, handleCouponResponse]);

	// --- Initial load ---

	useEffect(() => {
		checkAvailability();
	}, [checkAvailability]);

	// Set initial step after loading completes (only on first load).
	// handleCouponResponse may have already resolved to 'already-used' or 'revealed' —
	// only downgrade to 'not-available' when the response left us on the base 'available' path.
	const hasInitializedRef = useRef(false);
	useEffect(() => {
		if (isLoading) return;
		if (hasInitializedRef.current) return;
		hasInitializedRef.current = true;
		if (!isWithinHours) {
			setCouponStep(prev => (prev === 'available' ? 'not-available' : prev));
		}
	}, [isLoading, isWithinHours]);

	// Push claim time up so the parent (StopDetails) can show a live countdown on the trigger button
	useEffect(() => {
		if (!onClaimUpdate) return;
		if (couponStep === 'revealed' && claimData?.claimedAt) {
			onClaimUpdate(claimData.claimedAt);
		} else if (couponStep === 'already-used') {
			onClaimUpdate(null);
		}
	}, [couponStep, claimData?.claimedAt, onClaimUpdate]);

	// --- Barcode Scanner ---

	// Track last scanned barcode to avoid duplicate API calls
	const lastScannedRef = useRef('');

	const scannerReadyRef = useRef(false);

	const startScanner = useCallback(async () => {
		try {
			// Clear previous errors
			setScanError('');
			scannerReadyRef.current = false;

			// Verify the container exists in DOM
			const container = document.getElementById(scannerContainerId);
			if (!container) {
				console.error('Scanner container not found in DOM');
				setScanError(t('on_tour.stop.scan_failed') || 'Scanner failed to start. Please try again.');
				return;
			}

			const html5QrCode = new Html5Qrcode(scannerContainerId);
			scannerRef.current = html5QrCode;

			await html5QrCode.start(
				{ facingMode: 'environment' },
				{ fps: 10 },
				async (decodedText) => {
					// Ignore scans until camera has had time to focus
					if (!scannerReadyRef.current) return;

					// Avoid duplicate calls for same barcode
					if (decodedText === lastScannedRef.current) return;
					lastScannedRef.current = decodedText;

					// Send scanned barcode to backend for verification + redemption.
					// Stop the scanner regardless of result — on mismatch the user must
					// tap "Scan again" to restart, which avoids the setIsLoading remount
					// cycle re-triggering the same QR in an infinite loop.
					await verifyAndRedeem({ barcode: decodedText });
					try { html5QrCode.stop().catch(() => {}); } catch (e) {}
					scannerRef.current = null;
				},
				() => {} // Ignore scan failures (no barcode in frame)
			);

			// Give camera 2 seconds to focus before processing scans
			setTimeout(() => {
				scannerReadyRef.current = true;
			}, 2000);
		} catch (err: any) {
			const errMsg = err?.message || err?.toString() || '';
			console.error('Scanner error:', errMsg);
			if (errMsg.includes('NotAllowedError') || errMsg.includes('Permission denied')) {
				setScanError(t('on_tour.stop.camera_denied') || 'Camera access denied. Please check your browser permissions.');
			} else {
				setScanError(t('on_tour.stop.scan_failed') || 'Scanner failed to start. Please try again or use PIN.');
			}
		}
	}, [verifyAndRedeem, fallbackPin, t]);

	const stopScanner = useCallback(() => {
		if (scannerRef.current) {
			try {
				scannerRef.current.stop().catch(() => {});
			} catch (e) {
				// Scanner might not be running — ignore
			}
			scannerRef.current = null;
		}
	}, []);

	// Start/stop scanner when step changes
	useEffect(() => {
		if (couponStep === 'scan-barcode') {
			// Check if camera is likely available before attempting
			if (!navigator.mediaDevices?.getUserMedia) {
				console.warn('Camera API not available');
				setScanError(t('on_tour.stop.camera_denied') || 'Camera not available on this device.');
				return;
			}
			// Small delay to ensure DOM container is rendered
			const timer = setTimeout(() => startScanner(), 300);
			return () => {
				clearTimeout(timer);
				stopScanner();
			};
		}
	}, [couponStep, startScanner, stopScanner]);

	// --- PIN Entry ---

	const handlePinDigitChange = useCallback((index: number, value: string) => {
		if (value.length > 1) value = value.slice(-1);
		if (value && !/^\d$/.test(value)) return;

		setPinDigits(prev => {
			const next = [...prev];
			next[index] = value;
			return next;
		});

		// Auto-focus next input
		if (value && index < 5) {
			pinInputRefs.current[index + 1]?.focus();
		}
	}, []);

	const handlePinKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
		if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
			pinInputRefs.current[index - 1]?.focus();
		}
	}, [pinDigits]);

	const [pinError, setPinError] = useState('');

	const handlePinSubmit = useCallback(async () => {
		const pin = pinDigits.join('');
		if (pin.length !== 6) return;

		setPinError('');
		// Verify + redeem via backend in one call
		const success = await verifyAndRedeem({ pin });
		if (!success) {
			setPinError(t('on_tour.stop.wrong_pin') || 'Incorrect PIN. Try again.');
		}
	}, [pinDigits, verifyAndRedeem, t]);

	// --- Render Helpers ---

	const renderStopHeader = () => (
		<Box sx={{ px: 2, pt: 2 }}>
			{media.stop_tag && (
				<Box sx={{
					display: 'inline-block',
					bgcolor: COLORS.primary,
					color: 'white',
					borderRadius: '12px',
					px: 1.5,
					py: 0.3,
					fontSize: '0.75rem',
					fontWeight: 600,
					mb: 1,
				}}>
					{media.stop_tag}
				</Box>
			)}
			<Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>
				{state_activeStopState?.s_title}
			</Typography>
		</Box>
	);

	const renderProductImage = () => (
		<Box sx={{
			mx: 2,
			mt: 2,
			borderRadius: '12px',
			overflow: 'hidden',
			bgcolor: '#E8DDD0',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			minHeight: 160,
		}}>
			{media.product_pic ? (
				<img
					src={media.product_pic}
					alt={media.product_name}
					style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
				/>
			) : (
				<Box sx={{ width: 60, height: 60, opacity: 0.3 }}>
					<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="3" /><circle cx="8" cy="12" r="2" /></svg>
				</Box>
			)}
		</Box>
	);

	// --- Screen: Loading ---

	if (isLoading) {
		const loadingKey =
			couponStep === 'scan-barcode' ? 'on_tour.stop.checking_qrcode' :
			couponStep === 'enter-pin' ? 'on_tour.stop.checking_pincode' :
			'on_tour.stop.checking_coupon_access';
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', bgcolor: COLORS.bg, gap: 2, px: 3 }}>
				<CircularProgress sx={{ color: COLORS.primary }} />
				<Typography sx={{ color: COLORS.textSecondary, textAlign: 'center', fontSize: 14 }}>
					{t(loadingKey)}
				</Typography>
			</Box>
		);
	}

	// --- Screen 0/1: Coupon Overview (available or not available) ---

	const renderCouponOverview = () => (
		<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: COLORS.bg }} dir={isRTL ? 'rtl' : 'ltr'}>
			{renderStopHeader()}
			{renderProductImage()}

			<Box sx={{ px: 2, mt: 2 }}>
				<Typography variant="h6" sx={{ fontWeight: 700 }}>
					{media.product_name}
				</Typography>
				<Typography sx={{ color: COLORS.textSecondary, mt: 0.5, wordBreak: 'break-word' }}>
					{media.offer_in_details}
				</Typography>

				{/* Coupon count */}
				{tasteCount > 0 && (
					<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
						<Box sx={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: '35px',
							border: `1px solid ${COLORS.primary}`,
							borderRadius: '8px',
							px: 2.5,
							py: 0.75,
							fontSize: '1.2rem',
							color: COLORS.primary,
						}}>
							<i className="fa-solid fa-ticket" style={{ fontSize: '1.5rem' }} />
							<span>{tasteCount}</span>
						</Box>
					</Box>
				)}

				{isWithinHours ? (
					/* Hours range — available */
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
						<i className="fa-light fa-clock" style={{ color: COLORS.textSecondary }} />
						<Typography sx={{ color: COLORS.textSecondary, fontSize: '0.85rem' }}>
							{t('on_tour.stop.active')} {hoursRangeDisplay}
						</Typography>
					</Box>
				) : (
					/* Not available warning */
					<Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
						<Box sx={{ fontSize: '2rem' }}>
							<i className="fa-light fa-circle-exclamation" style={{ color: COLORS.primary }} />
						</Box>
						<Typography sx={{ fontWeight: 600, textAlign: 'center' }}>
							{t('on_tour.stop.not_available_yet') || 'Not available yet'} - {t('on_tour.stop.opens_at') || 'Opens at'} {openingTime}
						</Typography>
					</Box>
				)}
			</Box>

			{/* Buttons */}
			<Box sx={{ px: 2, mt: 'auto', pt: 1.5 }}>
				<Button
					fullWidth
					disabled={!isWithinHours}
					onClick={() => setCouponStep('confirm-reveal')}
					sx={{
						bgcolor: isWithinHours ? COLORS.primary : COLORS.disabledBg,
						color: 'white',
						borderRadius: '12px',
						py: 1.5,
						fontWeight: 600,
						textTransform: 'none',
						fontSize: '1rem',
						'&:hover': { bgcolor: isWithinHours ? COLORS.primaryDark : COLORS.disabledBg },
						'&.Mui-disabled': { color: 'white', bgcolor: COLORS.disabledBg },
					}}
				>
					{t('on_tour.stop.reveal_coupon') || 'Reveal coupon'}
				</Button>
				<Button
					fullWidth
					onClick={onClose}
					sx={{
						mt: 2,
						bgcolor: 'white',
						color: COLORS.primary,
						border: `1px solid ${COLORS.primary}`,
						borderRadius: '12px',
						py: 1.5,
						fontWeight: 600,
						textTransform: 'none',
						fontSize: '1rem',
						'&:hover': { bgcolor: '#FFF8F3' },
					}}
				>
					{t('on_tour.stop.close') || 'Close'}
				</Button>
			</Box>
		</Box>
	);

	// --- Screen 2: Confirm Reveal (Bottom Sheet) ---

	const renderConfirmReveal = () => (
		<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100%', bgcolor: COLORS.bg, px: 3, py: 4 }} dir={isRTL ? 'rtl' : 'ltr'}>
			<Typography variant="h6" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
				{t('on_tour.stop.before_revealing') || 'Before revealing'}
			</Typography>

			{/* Warning box */}
			<Box sx={{
				border: `1px solid ${COLORS.warningBorder}`,
				borderRadius: '12px',
				p: 2,
				bgcolor: COLORS.warningBg,
				display: 'flex',
				gap: 1.5,
				alignItems: 'flex-start',
			}}>
				<Box sx={{ fontSize: '1.2rem', mt: 0.3, flexShrink: 0 }}>
					<i className="fa-solid fa-triangle-exclamation" style={{ color: COLORS.primary }} />
				</Box>
				<Typography sx={{ fontSize: '0.9rem', color: COLORS.textPrimary, lineHeight: 1.6 }}>
					{t('on_tour.stop.barcode_reveal_description')}
				</Typography>
			</Box>

			{/* Confirm button */}
			<Button
				fullWidth
				onClick={() => {
					if (qrPayload) {
						setCouponStep('scan-barcode');
					} else if (fallbackPin) {
						setCouponStep('enter-pin');
					} else {
						redeemOldCoupon();
					}
				}}
				sx={{
					mt: 3,
					bgcolor: COLORS.primary,
					color: 'white',
					borderRadius: '12px',
					py: 1.5,
					fontWeight: 600,
					textTransform: 'none',
					fontSize: '1rem',
					'&:hover': { bgcolor: COLORS.primaryDark },
				}}
			>
				{t('on_tour.stop.barcode_reveal_button_yes')}
			</Button>

			{/* Cancel link */}
			<Typography
				onClick={() => setCouponStep('available')}
				sx={{
					textAlign: 'center',
					mt: 2,
					color: COLORS.textSecondary,
					cursor: 'pointer',
					textDecoration: 'underline',
					fontSize: '0.9rem',
					}}
				>
					{t('on_tour.stop.barcode_reveal_button_no')}
				</Typography>
		</Box>
	);

	// --- Screen 3: Barcode Scanner ---

	const renderBarcodeScanner = () => (
		<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: COLORS.bg }} dir={isRTL ? 'rtl' : 'ltr'}>
			{renderStopHeader()}

			<Box sx={{ px: 2, mt: 2 }}>
				<Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
					{t('on_tour.stop.scan_barcode_of') || 'Scan the barcode of'} {partnerName}
				</Typography>
			</Box>

			{/* Scanner viewport */}
			<Box sx={{
				mx: 2,
				mt: 2,
				borderRadius: '16px',
				overflow: 'hidden',
				bgcolor: COLORS.scannerBg,
				position: 'relative',
				minHeight: 320,
			}}>
				<div id={scannerContainerId} style={{ width: '100%', minHeight: 320 }} />

				{/* Scanning frame overlay */}
				{!scanError && (
					<Box sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: 235,
						height: 200,
						border: `2px solid ${COLORS.scannerFrame}`,
						borderRadius: '8px',
						pointerEvents: 'none',
						'&::after': {
							content: '""',
							position: 'absolute',
							top: '50%',
							left: '10%',
							width: '80%',
							height: '2px',
							bgcolor: COLORS.scannerFrame,
							animation: 'scanLine 2s ease-in-out infinite',
						},
						'@keyframes scanLine': {
							'0%, 100%': { top: '20%' },
							'50%': { top: '80%' },
						},
					}} />
				)}

				{/* Error overlay */}
				{scanError && (
					<Box sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: 235,
						height: 200,
						border: `2px solid ${COLORS.scannerError}`,
						borderRadius: '8px',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						pointerEvents: 'none',
					}}>
						<Box sx={{ fontSize: '2rem', color: COLORS.scannerError }}>
							<i className="fa-solid fa-circle-xmark" />
						</Box>
					</Box>
				)}
			</Box>

			{/* Status text */}
			<Box sx={{ px: 2, mt: 2, textAlign: 'center' }}>
				{scanError ? (
					<>
						<Typography sx={{ fontSize: '0.85rem', color: COLORS.errorBorder, textAlign: 'center', mb: 2 }}>
							{scanError}
						</Typography>
						<Button
							onClick={() => {
								lastScannedRef.current = '';
								setScanError('');
								startScanner();
							}}
							sx={{
								bgcolor: COLORS.primary,
								color: 'white',
								borderRadius: '12px',
								py: 1,
								px: 3,
								fontWeight: 600,
								textTransform: 'none',
								fontSize: '0.9rem',
								'&:hover': { bgcolor: COLORS.primaryDark },
							}}
						>
							{t('on_tour.stop.scan_again') || 'Scan again'}
						</Button>
					</>
				) : null}

			</Box>

			{/* PIN fallback link — only show if fallbackPin exists */}
			{fallbackPin && (
				<Box sx={{ px: 2, mt: 2, pb: 3, textAlign: 'center' }}>
					<Typography
						onClick={() => {
							stopScanner();
							setCouponStep('enter-pin');
						}}
						sx={{
							color: COLORS.primary,
							cursor: 'pointer',
							textDecoration: 'underline',
							fontSize: '0.9rem',
						}}
					>
						{t('on_tour.stop.request_pin_instead') || 'Request PIN from staff instead'}
					</Typography>
				</Box>
			)}
		</Box>
	);

	// --- Screen 3ג: PIN Entry ---

	const renderPinEntry = () => (
		<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: COLORS.bg }} dir={isRTL ? 'rtl' : 'ltr'}>
			{renderStopHeader()}

			<Box sx={{ px: 2, mt: 3 }}>
				<Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
					{t('on_tour.stop.enter_6_digit_pin') || 'Enter 6 digit PIN'}
				</Typography>
			</Box>

			{/* PIN input boxes */}
			<Box dir="ltr" sx={{ display: 'flex', justifyContent: 'center', gap: 1, mx: 2, mt: 3 }}>
				{pinDigits.map((digit, i) => (
					<TextField
						key={i}
						inputRef={(el: HTMLInputElement | null) => { pinInputRefs.current[i] = el; }}
						value={digit}
						onChange={(e) => handlePinDigitChange(i, e.target.value)}
						onKeyDown={(e) => handlePinKeyDown(i, e as React.KeyboardEvent)}
						inputProps={{
							maxLength: 1,
							style: {
								textAlign: 'center',
								fontSize: '1.5rem',
								fontWeight: 700,
								padding: '12px 0',
							},
							inputMode: 'numeric',
						}}
						sx={{
							width: 48,
							'& .MuiOutlinedInput-root': {
								borderRadius: '8px',
								bgcolor: 'white',
							},
						}}
					/>
				))}
			</Box>

			{/* Info note */}
			<Box sx={{
				mx: 2,
				mt: 3,
				p: 2,
				bgcolor: COLORS.warningBg,
				borderRadius: '12px',
				display: 'flex',
				gap: 1,
				alignItems: 'flex-start',
			}}>
				<i className="fa-light fa-circle-info" style={{ color: COLORS.primary, marginTop: 2 }} />
				<Typography sx={{ fontSize: '0.85rem', color: COLORS.textPrimary }}>
					{t('on_tour.stop.timer_starts_on_pin') || 'Timer starts when PIN is confirmed.'}
				</Typography>
			</Box>

			{/* PIN error message */}
			{pinError && (
				<Box sx={{ mx: 2, mt: 2, p: 2, bgcolor: COLORS.errorBg, border: `1px solid ${COLORS.errorBorder}`, borderRadius: '12px' }}>
					<Typography sx={{ fontSize: '0.85rem', color: COLORS.textPrimary, textAlign: 'center' }}>
						{pinError}
					</Typography>
				</Box>
			)}

			{/* Submit button */}
			<Box sx={{ px: 2, mt: 3 }}>
				<Button
					fullWidth
					onClick={handlePinSubmit}
					disabled={pinDigits.some(d => !d)}
					sx={{
						bgcolor: COLORS.primary,
						color: 'white',
						borderRadius: '12px',
						py: 1.5,
						fontWeight: 600,
						textTransform: 'none',
						fontSize: '1rem',
						'&:hover': { bgcolor: COLORS.primaryDark },
						'&.Mui-disabled': { bgcolor: COLORS.disabledBg, color: 'white' },
					}}
				>
					{t('on_tour.stop.confirm_pin') || 'Confirm PIN'}
				</Button>
			</Box>

			{/* Back to scanning link — only show if qrPayload exists */}
			{qrPayload && (
				<Box sx={{ px: 2, mt: 2, pb: 3, textAlign: 'center' }}>
					<Typography
						onClick={() => {
							setScanError('');
							setCouponStep('scan-barcode');
						}}
						sx={{
							color: COLORS.primary,
							cursor: 'pointer',
							textDecoration: 'underline',
							fontSize: '0.9rem',
						}}
					>
						{t('on_tour.stop.back_to_scanning') || 'Back to scanning'}
					</Typography>
				</Box>
			)}
		</Box>
	);

	// --- Screen 4: Revealed ---

	const renderRevealed = () => (
		<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: COLORS.bg }} dir={isRTL ? 'rtl' : 'ltr'}>
			{renderStopHeader()}

			{/* Coupon card */}
			<Box sx={{
				mx: 2,
				mt: 2,
				border: '2px dashed #C4B5A5',
				borderRadius: '16px',
				bgcolor: 'white',
				p: 3,
				textAlign: 'center',
			}}>
				<Typography sx={{ color: COLORS.textSecondary, fontSize: '0.8rem', mb: 1 }}>
					{t('on_tour.stop.your_benefit') || 'Your benefit'}
				</Typography>
				<Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 2, mb: 2 }}>
					{media.product_barcode}
				</Typography>

				{/* Timer */}
				<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 1 }}>
					<i className="fa-light fa-hourglass" style={{ color: COLORS.primary, fontSize: '1.2rem' }} />
					<Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: COLORS.primary }}>
						{timeLeft || '12:00'}
					</Typography>
				</Box>
			</Box>

			{/* Info section */}
			<Box sx={{ px: 2, mt: 3, textAlign: 'center' }}>
				<Typography sx={{ color: COLORS.textSecondary, fontSize: '0.85rem' }}>
					{t('on_tour.stop.show_to_staff') || 'Show to a staff member'}
				</Typography>

				<Typography sx={{ color: COLORS.textSecondary, fontSize: '0.8rem', mt: 0.5 }}>
					{t('on_tour.stop.use_before_expires') || 'Use before it expires'}
				</Typography>

				<Typography sx={{ color: COLORS.textSecondary, fontSize: '0.75rem', mt: 1.5, textAlign: 'start' }}>
					{t('on_tour.stop.revealed_at') || 'Revealed'} · {formattedRevealDate}
				</Typography>
			</Box>

			{/* Offline warning */}
			{!isOnline && (
				<Box sx={{
					mx: 2,
					mt: 2,
					p: 2,
					bgcolor: COLORS.warningBg,
					border: `1px solid ${COLORS.warningBorder}`,
					borderRadius: '12px',
					display: 'flex',
					gap: 1.5,
					alignItems: 'flex-start',
				}}>
					<Box sx={{ fontSize: '1rem', mt: 0.2 }}>
						<i className="fa-solid fa-xmark" style={{ color: COLORS.primary }} />
					</Box>
					<Typography sx={{ fontSize: '0.85rem', color: COLORS.textPrimary, lineHeight: 1.6 }}>
						{t('on_tour.stop.offline_coupon_warning') || 'No internet connection. Your coupon is valid — the timer continues to run. Connect to confirm at the business.'}
					</Typography>
				</Box>
			)}

			{/* Offline timer indicator */}
			{!isOnline && (
				<Box sx={{ textAlign: 'center', mt: 2 }}>
					<Typography sx={{ color: COLORS.textSecondary, fontSize: '0.85rem' }}>
						{t('on_tour.stop.timer_still_running') || 'Timer still running'}
					</Typography>
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 0.5 }}>
						<Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
							{timeLeft || '00:00'}
						</Typography>
						<i className="fa-light fa-hourglass-end" style={{ color: COLORS.primary }} />
					</Box>
				</Box>
			)}

			{/* Close button — timer keeps running in background; reopening from StopDetails restores this screen */}
			<Box sx={{ px: 2, mt: 'auto', pt: 3, pb: 2 }}>
				<Button
					fullWidth
					onClick={onClose}
					sx={{
						bgcolor: 'white',
						color: COLORS.primary,
						border: `1px solid ${COLORS.primary}`,
						borderRadius: '12px',
						py: 1.5,
						fontWeight: 600,
						textTransform: 'none',
						fontSize: '1rem',
						'&:hover': { bgcolor: '#FFF8F3' },
					}}
				>
					{t('on_tour.stop.close') || 'Close'}
				</Button>
			</Box>
			</Box>
	);

	// --- Screen 5: Already Used ---

	const renderAlreadyUsed = () => (
		<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: COLORS.bg }} dir={isRTL ? 'rtl' : 'ltr'}>
			{renderStopHeader()}

			{/* Greyed-out image area */}
			<Box sx={{
				mx: 2,
				mt: 2,
				borderRadius: '12px',
				overflow: 'hidden',
				bgcolor: '#E8DDD0',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				minHeight: 160,
				position: 'relative',
			}}>
				{media.product_pic && (
					<img
						src={media.product_pic}
						alt=""
						style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', opacity: 0.3, filter: 'grayscale(1)' }}
					/>
				)}
				<Box sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					fontSize: '3rem',
					color: '#999',
				}}>
					<i className="fa-solid fa-xmark" />
				</Box>
			</Box>

			{/* Status */}
			<Box sx={{ px: 2, mt: 3, textAlign: 'center' }}>
				<Box sx={{ fontSize: '1.5rem', color: COLORS.primary, mb: 1 }}>
					<i className="fa-solid fa-circle-check" />
				</Box>
				<Typography variant="h6" sx={{ fontWeight: 700 }}>
					{t('on_tour.stop.coupon_already_used') || 'Coupon already used'}
				</Typography>
				<Typography sx={{ color: COLORS.textSecondary, mt: 1, fontSize: '0.9rem' }}>
					{formattedRevealDate && (
						<>
							{t('on_tour.stop.revealed_at') || 'Revealed'} {formattedRevealDate}
							<br />
							{t('on_tour.stop.enjoy') || 'Enjoy!'}
						</>
					)}
				</Typography>
			</Box>

			<Box sx={{ px: 2, mt: 'auto', pt: 3, pb: 2 }}>
				<Button
					fullWidth
					onClick={onClose}
					sx={{
						bgcolor: 'white',
						color: COLORS.primary,
						border: `1px solid ${COLORS.primary}`,
						borderRadius: '12px',
						py: 1.5,
						fontWeight: 600,
						textTransform: 'none',
						fontSize: '1rem',
						'&:hover': { bgcolor: '#FFF8F3' },
					}}
				>
					{t('on_tour.stop.close') || 'Close'}
				</Button>
			</Box>
			</Box>
	);

	// --- Main Render (State Machine) ---

	switch (couponStep) {
		case 'not-available':
		case 'available':
			return renderCouponOverview();
		case 'confirm-reveal':
			return renderConfirmReveal();
		case 'scan-barcode':
			return renderBarcodeScanner();
		case 'enter-pin':
			return renderPinEntry();
		case 'revealed':
			return renderRevealed();
		case 'already-used':
			return renderAlreadyUsed();
		default:
			return renderCouponOverview();
	}
});

export default CouponPage;
